import { Injectable } from '@angular/core';

import type { DropdownData, FilteredValue, Value } from '../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../model/reactive-form-value.utils';

export type ReactiveOptionStoreEntry = {
    readonly key: string;
    readonly value: unknown;
    readonly label: string;
    readonly raw: unknown;
    readonly group?: string;
    readonly disabled?: boolean;
};

type DropdownOption = Value<unknown> | FilteredValue<unknown>;

/** Stores canonical dropdown options by field so controls can keep primitive ids while UI code can resolve full entities. */
@Injectable()
export class ReactiveOptionStoreService {
    private readonly optionsByField = new Map<string, Map<string, ReactiveOptionStoreEntry>>();

    public setOptions(
        fieldName: string,
        data: DropdownData<unknown>,
        primaryKey = 'id',
        identityKeys: readonly string[] = [],
    ): void {
        if (!fieldName.trim()) {
            return;
        }

        const options = ReactiveFormValueUtils.getDropdownOptions(data);
        const indexedOptions = new Map<string, ReactiveOptionStoreEntry>();
        const ambiguousKeys = new Set<string>();

        for (const option of options) {
            const entries = this.toEntries(option as DropdownOption, primaryKey, identityKeys);

            for (const entry of entries) {
                if (ambiguousKeys.has(entry.key)) {
                    continue;
                }

                const existingEntry = indexedOptions.get(entry.key);

                if (!existingEntry) {
                    indexedOptions.set(entry.key, entry);
                    continue;
                }

                if (this.areEntriesSameCanonicalValue(existingEntry, entry, primaryKey, identityKeys)) {
                    indexedOptions.set(entry.key, entry);
                    continue;
                }

                indexedOptions.delete(entry.key);
                ambiguousKeys.add(entry.key);
            }
        }

        this.optionsByField.set(fieldName, indexedOptions);
    }

    public getOption(
        fieldName: string,
        value: unknown,
        primaryKey = 'id',
        identityKeys: readonly string[] = [],
    ): ReactiveOptionStoreEntry | null {
        const fieldOptions = this.optionsByField.get(fieldName);

        if (!fieldOptions) {
            return null;
        }

        const comparableValue = this.toComparableKey(value, primaryKey, identityKeys);

        return comparableValue ? fieldOptions.get(comparableValue) ?? null : null;
    }

    public getRawValue<T>(
        fieldName: string,
        value: unknown,
        primaryKey = 'id',
        identityKeys: readonly string[] = [],
    ): T | null {
        const option = this.getOption(fieldName, value, primaryKey, identityKeys);

        return (option?.raw ?? null) as T | null;
    }

    public clear(fieldName: string): void {
        this.optionsByField.delete(fieldName);
    }

    private toEntries(
        option: DropdownOption,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): ReactiveOptionStoreEntry[] {
        const keys = ReactiveFormValueUtils.getOptionComparableKeys(option, primaryKey, identityKeys);

        return keys.map((key) => ({
            key,
            value: option.value,
            label: String(option.label ?? ''),
            raw: option.raw ?? option.value,
            group: String(option.group ?? ''),
            disabled: Boolean(option.disabled ?? false),
        }));
    }

    private areEntriesSameCanonicalValue(
        first: ReactiveOptionStoreEntry,
        second: ReactiveOptionStoreEntry,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): boolean {
        return this.toCanonicalComparableValue(first.value, primaryKey, identityKeys)
            === this.toCanonicalComparableValue(second.value, primaryKey, identityKeys);
    }

    private toCanonicalComparableValue(value: unknown, primaryKey: string, identityKeys: readonly string[] = []): string {
        return this.toComparableKey(value, primaryKey, identityKeys) ?? JSON.stringify(value ?? null);
    }

    private toComparableKey(value: unknown, primaryKey: string, identityKeys: readonly string[] = []): string | null {
        const comparableId = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);

        return comparableId === null ? null : String(comparableId);
    }
}
